import React, { useCallback, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { useStreamVideo } from '../media';
import { useInterviewSession } from './useInterviewSession';
import { useProctor } from './useProctor';
import { AiStage, RoomControls, RoomTimer, SegmentRail } from './RoomParts';

const EndDialog = ({ open, onOpenChange, onConfirm }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="iv" data-testid="end-interview-dialog">
      <DialogHeader>
        <DialogTitle>Submit and end your interview?</DialogTitle>
        <DialogDescription>Your answers so far are saved and sent to the hiring team. You won't be able to continue afterwards.</DialogDescription>
      </DialogHeader>
      <div className="iv-row-end">
        <button type="button" className="iv-btn iv-btn-ghost" onClick={() => onOpenChange(false)} data-testid="end-interview-cancel-btn">Keep going</button>
        <button type="button" className="iv-btn iv-btn-primary" onClick={onConfirm} data-testid="candidate-end-confirm-btn">Submit & end</button>
      </div>
    </DialogContent>
  </Dialog>
);

const ProctorBadge = ({ notice }) => (
  <span className="iv-proctor" data-testid="proctoring-indicator" title="Integrity checks only route an interview to human review">
    <i style={notice ? { background: '#F59E0B' } : undefined} />
    {notice ? 'Proctoring active (Attention)' : 'Proctoring active (Normal)'}
  </span>
);

export default function InterviewRoom({ ctx, token, media, onFinished, onInterrupted }) {
  const [videoRef, setVideo] = useStreamVideo(media.stream);
  const [captions, setCaptions] = useState(true);
  const [endOpen, setEndOpen] = useState(false);
  const ensureStream = useCallback(() => media.ensure(), [media]);
  const s = useInterviewSession({ token, ensureStream, streamRef: media.ref, onFinished, onInterrupted });
  const proctor = useProctor({
    token, ctx, active: s.started && s.phase !== 'ending', videoRef, streamRef: media.ref, clock: s.clock,
    descriptor: ctx.identity_descriptor,
  });

  return (
    <div className="iv iv-room" data-testid="interview-room">
      <header className="iv-room-top">
        <div className="iv-row" style={{ gap: '0.5rem', minWidth: 0 }}>
          <img src="/chire-icon.png" alt="CHIRE" width={24} height={24} style={{ borderRadius: 5 }} />
          <span style={{ fontWeight: 700, fontSize: '0.88rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ctx.job_title}</span>
        </div>
        <div className="iv-row" style={{ gap: '0.75rem' }}>
          <ProctorBadge notice={proctor.notice} />
          <RoomTimer remaining={s.remaining} running={s.started} />
        </div>
      </header>
      <SegmentRail segments={s.srv?.segments || ctx.state?.segments} />
      {(proctor.notice || proctor.fsOut) && (
        <div style={{ padding: '0.6rem 1rem 0' }}>
          <div className="iv-note is-warn" data-testid="proctor-notice">
            {proctor.fsOut ? 'This interview needs full screen.' : proctor.notice}
            {proctor.fsOut && <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" style={{ marginLeft: '0.6rem' }} onClick={proctor.backToFullscreen} data-testid="proctor-fullscreen-btn">Return to full screen</button>}
          </div>
        </div>
      )}
      <div className="iv-stage">
        <AiStage s={s} captions={captions} ctx={ctx} />
        <div className="iv-side">
          <div className="iv-self">
            <video ref={setVideo} autoPlay muted playsInline data-testid="interview-self-video" />
          </div>
          <div className="iv-note" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.8)', borderColor: 'rgba(255,255,255,0.1)' }}>
            Speak naturally. You can ask the interviewer to repeat or clarify, take a moment to think, or type an answer instead. Gaze and emotions are never analysed.
          </div>
        </div>
      </div>
      <RoomControls s={s} captions={captions} setCaptions={setCaptions} onEnd={() => setEndOpen(true)} />
      <EndDialog open={endOpen} onOpenChange={setEndOpen} onConfirm={() => { setEndOpen(false); s.controls.end(); }} />
    </div>
  );
}
